/**
 * Der Fehler, wie ihn die Schnittstelle liefert.
 *
 * docs/05-api-konventionen.md, Abschnitt 4. Wichtig ist der Satz dort:
 * "Das Frontend wertet `code` aus, niemals `title` oder `detail`."
 *
 * `title` und `detail` kommen vom Server auf Deutsch und sind zur Anzeige
 * gedacht. Sie duerfen sich jederzeit aendern -- eine Verzweigung im Code, die
 * auf ihren Wortlaut baut, braeche beim naechsten Formulierungswechsel.
 * Deshalb: Anzeige aus `src/i18n/de.json` unter `errors.<CODE>`, mit dem
 * Servertext nur als Rueckfall fuer Codes, die die Oberflaeche noch nicht
 * kennt.
 */

export interface FieldProblem {
  field: string;
  code: string;
  message: string;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string;
  request_id: string | null;
  errors?: FieldProblem[];
  expected_version?: number;
  actual_version?: number;
  current?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly requestId: string | null;
  readonly fieldErrors: FieldProblem[];
  readonly problem: ProblemDetails | null;

  constructor(problem: ProblemDetails | null, status: number, fallbackCode = 'INTERNAL_ERROR') {
    super(problem?.detail ?? `HTTP ${String(status)}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem?.code ?? fallbackCode;
    this.detail = problem?.detail ?? '';
    this.requestId = problem?.request_id ?? null;
    this.fieldErrors = problem?.errors ?? [];
    this.problem = problem;
  }

  /** Ein Fehler, der sich durch erneutes Anmelden beheben laesst. */
  get isAuthenticationProblem(): boolean {
    return this.status === 401;
  }

  /**
   * Ein Fehler, bei dem ein zweiter Versuch sinnlos ist.
   *
   * Alles unterhalb von 500 ausser 429: Der Server hat verstanden und
   * abgelehnt. TanStack Query soll das nicht wiederholen -- eine Wiederholung
   * fuehrt zu denselben 403 und verzoegert nur die Fehlermeldung.
   */
  get isPermanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 429;
  }
}

/** Kein Kontakt zum Server -- unterschieden vom abgelehnten Aufruf. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('network-unreachable');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
